import type { CallCorpusRequest, SendToLoopRequest } from '@jabberr/core';
import type { ProfessionalAvailability, ProfessionalStatus, ProfessionalSummary } from './professional.js';
// Typed fetch client for the Jabberr api (prefix /api, Better Auth session cookie).
import type {
  AddMemberRequest,
  LabelDeckResponse,
  LabelDeckFilter,
  LabelReviewFilter,
  LabelReviewResponse,
  PutLabelRequest,
  AuthConfigResponse,
  CancelTurnResponse,
  ChunkUploadResponse,
  CompleteRecordingRequest,
  ContextResponse,
  ContextVersionResponse,
  ConversationDetailResponse,
  ConversationListResponse,
  ConversationResponse,
  CreateConversationRequest,
  CreateMemoryRequest,
  CreateProjectRequest,
  CreateRecordingRequest,
  CreateRecordingResponse,
  CreateMeetingRequest,
  CreateMeetingResponse,
  ExtractMemoryResponse,
  FeedbackConfigResponse,
  AdminCreateUserRequest,
  AdminUserListResponse,
  AdminUserResponse,
  DocumentPromptsResponse,
  HealthResponse,
  ImportVttRequest,
  MeResponse,
  MemberListResponse,
  MemberResponse,
  MemoryItemResponse,
  MemoryListResponse,
  MeetingListResponse,
  MeetingResponse,
  MentionsResponse,
  RecentConversationsResponse,
  Preferences,
  PreferencesResponse,
  StartIdeaRequest,
  StartIdeaResponse,
  UseSuggestionResponse,
  BuildParkingLotItemResponse,
  CreateParkingLotItemRequest,
  ParkFromConversationResponse,
  ParkingLotCountResponse,
  ParkingLotDetailResponse,
  ParkingLotItemResponse,
  ParkingLotListResponse,
  PostParkingLotCommentResponse,
  TalkParkingLotItemResponse,
  UpdateParkingLotItemRequest,
  MeetingSegment,
  MeetingStatus,
  MeetingTranscriptResponse,
  OutputListResponse,
  RatedRepliesResponse,
  TurnRating,
  OutputResponse,
  SaveToWorkbenchResponse,
  ProjectListResponse,
  ProjectResponse,
  PinContextRequest,
  PinContextResponse,
  PublishContextRequest,
  PutStateDocRequest,
  PutStateDocResponse,
  RecordingDetailResponse,
  RecordingListResponse,
  RoomInviteResponse,
  ReviewRecordingResponse,
  SearchResponse,
  RegenerateResponse,
  SendMessageRequest,
  SendMessageResponse,
  StateDocKind,
  StateDocsResponse,
  TranscriptResponse,
  TurnResponse,
  UpdateConversationRequest,
  UpdateMemoryRequest,
  UpdateProjectRequest,
  UpdateSpeakersRequest,
  UpdateMeetingRequest,
  UpdateTurnRatingRequest,
  UpdateTurnRequest,
  WriteRequest,
  WriteResponse,
} from '@jabberr/core';

export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

type Listener = () => void;
const unauthorizedListeners = new Set<Listener>();
/** Subscribe to 401 responses (used to bounce the app to /login). */
export function onUnauthorized(fn: Listener): () => void {
  unauthorizedListeners.add(fn);
  return () => unauthorizedListeners.delete(fn);
}

export function notifyUnauthorized(): void {
  unauthorizedListeners.forEach((fn) => fn());
}

async function readError(res: Response): Promise<ApiError> {
  let body: unknown;
  let code = `http_${res.status}`;
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    body = await res.json();
    if (body && typeof body === 'object') {
      const b = body as { error?: unknown; message?: unknown };
      if (typeof b.error === 'string') code = b.error;
      if (typeof b.message === 'string') message = b.message;
      else if (typeof b.error === 'string') message = b.error;
    }
  } catch {
    // non-JSON error body
  }
  return new ApiError(res.status, code, message, body);
}

async function request<T>(method: string, path: string, body?: unknown, signal?: AbortSignal): Promise<T> {
  const init: RequestInit = { method, headers: {}, credentials: 'same-origin' };
  if (signal) init.signal = signal;
  if (body !== undefined) {
    init.headers = { 'content-type': 'application/json' };
    init.body = JSON.stringify(body);
  }
  const res = await fetch(`/api${path}`, init);
  if (res.status === 401) notifyUnauthorized();
  if (!res.ok) throw await readError(res);
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export type PresenceUser = {
  id: string;
  name: string;
  email: string;
  status: 'online' | 'offline' | 'unavailable';
  hasActiveSession?: boolean;
};

export type RoomInvite = {
  conversationId?: string;
  meetingId?: string;
  title?: string;
  invitedBy: string;
};

export { type RoomInviteResponse } from '@jabberr/core';

export const api = {
  professional: {
    availability: () => request<ProfessionalAvailability>('GET', '/me/professional'),
    get: (meetingId: string) => request<ProfessionalStatus>('GET', `/meetings/${meetingId}/professional`),
    summary: (meetingId: string) => request<ProfessionalSummary>('GET', `/meetings/${meetingId}/professional/summary`),
    start: (meetingId: string, maxSeconds: number, sessionId?: string) => request<ProfessionalStatus>('POST', `/meetings/${meetingId}/professional`, { maxSeconds, disclosureAccepted: true, sessionId }),
    extend: (meetingId: string, sessionId: string, expectedMaxSeconds: number) => request<ProfessionalStatus>('POST', `/meetings/${meetingId}/professional/extend`, { sessionId, expectedMaxSeconds }),
    stop: (meetingId: string, sessionId?: string) => sessionId
      ? request<ProfessionalStatus>('DELETE', `/meetings/${meetingId}/professional?sessionId=${encodeURIComponent(sessionId)}`)
      : request<ProfessionalStatus>('DELETE', `/meetings/${meetingId}/professional`),
  },
  me: () => request<MeResponse>('GET', '/me'),
  appearance: {
    /** GET /api/me/appearance: the caller's own assistant artwork (Tarawa team only; 403 otherwise). */
    mine: () => request<{ jabberrAvatarId: string; jabberrAssistantImageId?: string }>('GET', '/me/appearance'),
    setMine: (jabberrAvatarId: string) => request<{ jabberrAvatarId: string; jabberrAssistantImageId?: string }>('PATCH', '/me/appearance', { jabberrAvatarId }),
  },
  mentions: {
    /** GET /api/me/mentions: unread @mentions of the caller, newest first, at most 50. */
    list: () => request<MentionsResponse>('GET', '/me/mentions'),
  },
  presence: {
    users: () => request<{ users: PresenceUser[] }>('GET', '/presence/users'),
    setStatus: (status: 'online' | 'offline' | 'unavailable') =>
      request<{ status: 'online' | 'offline' | 'unavailable' }>('PUT', '/presence/status', { status }),
    invites: () => request<{ invites: RoomInvite[] }>('GET', '/presence/invites'),
    clearInvite: (meetingId: string) => request<undefined>('DELETE', `/presence/invites/${meetingId}`),
  },
  search: (q: string, signal?: AbortSignal) =>
    request<SearchResponse>('GET', `/search?q=${encodeURIComponent(q)}`, undefined, signal),
  authConfig: () => request<AuthConfigResponse>('GET', '/auth-config'),
  health: (signal?: AbortSignal) => request<HealthResponse>('GET', '/health', undefined, signal),
  devMagicLink: (email: string) => request<{ url: string }>('GET', `/dev/magic-link?email=${encodeURIComponent(email)}`),

  /** Emailed project invitations for client guests (Owners only; GUEST_INVITE_EMAILS_ENABLED). */
  guestInvitations: {
    list: (projectId: string) => request<{ invitations: import('@jabberr/core').GuestInvitation[] }>('GET', `/projects/${projectId}/guest-invitations`),
    send: (projectId: string, body: { name: string; email: string; role: import('@jabberr/core').GuestInvitationRole }) =>
      request<{ invitation: import('@jabberr/core').GuestInvitation }>('POST', `/projects/${projectId}/guest-invitations`, body),
    revoke: (projectId: string, id: string) => request<undefined>('DELETE', `/projects/${projectId}/guest-invitations/${id}`),
  },
  projects: {
    list: () => request<ProjectListResponse>('GET', '/projects'),
    create: (body: CreateProjectRequest) => request<ProjectResponse>('POST', '/projects', body),
    get: (projectId: string) => request<ProjectResponse>('GET', `/projects/${projectId}`),
    update: (projectId: string, body: UpdateProjectRequest) =>
      request<ProjectResponse>('PATCH', `/projects/${projectId}`, body),
    members: (projectId: string) => request<MemberListResponse>('GET', `/projects/${projectId}/members`),
    addMember: (projectId: string, body: AddMemberRequest) =>
      request<MemberResponse>('POST', `/projects/${projectId}/members`, body),
    /** Remove a person from this project (owners only). */
    removeMember: (projectId: string, userId: string) =>
      request<{ removed: true }>('DELETE', `/projects/${projectId}/members/${encodeURIComponent(userId)}`),
    /** Link to the project's Anchor Yard record (OPEN-34): account and Workbench project come from Yard. */
    linkAnchor: (projectId: string, workbenchProjectId?: string) =>
      request<ProjectResponse & { workbenchProjectIds: string[] }>('POST', `/projects/${projectId}/anchor-link`, workbenchProjectId ? { workbenchProjectId } : {}),
    addEveryone: (projectId: string) => request<{ added: number }>('POST', `/projects/${projectId}/members/everyone`),
    /** Draft a document across threads: returns a fresh conversation with its seeded first turn. */
    write: (projectId: string, body: WriteRequest) => request<WriteResponse>('POST', `/projects/${projectId}/write`, body),
  },

  labelling: {
    deck: (limit = 20, filter: LabelDeckFilter = 'all') => request<LabelDeckResponse>('GET', `/labelling/deck?limit=${limit}&filter=${filter}`),
    put: (body: PutLabelRequest) => request<{ ok: true }>('POST', '/labelling/labels', body),
    remove: (key: string) => request<{ ok: true }>('DELETE', `/labelling/labels?key=${encodeURIComponent(key)}`),
    review: (filter: LabelReviewFilter) => request<LabelReviewResponse>('GET', `/labelling/review?filter=${filter}`),
  },

  admin: {
    users: () => request<AdminUserListResponse>('GET', '/admin/users'),
    createUser: (body: AdminCreateUserRequest) => request<AdminUserResponse>('POST', '/admin/users', body),
  },

  state: {
    get: (projectId: string) => request<StateDocsResponse>('GET', `/projects/${projectId}/state`),
    put: (projectId: string, kind: StateDocKind, body: PutStateDocRequest) =>
      request<PutStateDocResponse>('PUT', `/projects/${projectId}/state/${kind}`, body),
  },

  memory: {
    list: (projectId: string) => request<MemoryListResponse>('GET', `/projects/${projectId}/memory`),
    create: (projectId: string, body: CreateMemoryRequest) => request<MemoryItemResponse>('POST', `/projects/${projectId}/memory`, body),
    update: (memoryId: string, body: UpdateMemoryRequest) => request<MemoryItemResponse>('PATCH', `/memory/${memoryId}`, body),
    /** Reads the exchanges no extraction has covered yet. Takes as long as one short model call. */
    extract: (conversationId: string) => request<ExtractMemoryResponse>('POST', `/conversations/${conversationId}/memory/extract`),
  },

  outputs: {
    documents: () => request<DocumentPromptsResponse>('GET', '/prompts/documents'),
    /** Keep any completed reply on the Outputs page, or drop it again. */
    saveTurn: (conversationId: string, turnId: string, body: UpdateTurnRequest) =>
      request<TurnResponse>('PATCH', `/conversations/${conversationId}/turns/${turnId}`, body),
    /** Thumbs on a completed reply; rating null clears it. */
    rateTurn: (conversationId: string, turnId: string, body: UpdateTurnRatingRequest) =>
      request<TurnResponse>('PUT', `/conversations/${conversationId}/turns/${turnId}/rating`, body),
    list: (projectId: string) => request<OutputListResponse>('GET', `/projects/${projectId}/outputs`),
    /** Rated replies for review, newest first, each with a smoke-set case draft. */
    rated: (projectId: string, rating: TurnRating) => request<RatedRepliesResponse>('GET', `/projects/${projectId}/rated-replies?rating=${rating}`),
    get: (projectId: string, turnId: string) => request<OutputResponse>('GET', `/projects/${projectId}/outputs/${turnId}`),
    /** Save to the Workbench (OPEN-34): once per output; a second call returns the same item. */
    saveToWorkbench: (projectId: string, turnId: string) =>
      request<SaveToWorkbenchResponse>('POST', `/projects/${projectId}/outputs/${turnId}/workbench`),
  },

  feedback: {
    /** Whether the shared visual feedback overlay is on, and where its widget lives. */
    config: () => request<FeedbackConfigResponse>('GET', '/feedback/config'),
  },

  context: {
    get: (projectId: string, audience?: 'internal' | 'client') =>
      request<ContextResponse>('GET', audience ? `/projects/${projectId}/context?audience=${audience}` : `/projects/${projectId}/context`),
    version: (projectId: string, version: number, audience?: 'internal' | 'client') =>
      request<ContextVersionResponse>('GET', audience ? `/projects/${projectId}/context/${version}?audience=${audience}` : `/projects/${projectId}/context/${version}`),
    publish: (projectId: string, body: PublishContextRequest) =>
      request<ContextVersionResponse>('POST', `/projects/${projectId}/context`, body),
    /** Inserts "- text" under `heading` in the latest context and publishes it as the next version. */
    pin: (projectId: string, body: PinContextRequest) =>
      request<PinContextResponse>('POST', `/projects/${projectId}/context/pins`, body),
  },

  /** The parking lot (OPEN-38): the board, one idea with its comments, and what can be done with it. */
  parkingLot: {
    list: (filter: { project?: string; column?: string } = {}) => {
      const q = new URLSearchParams(Object.entries(filter).filter(([, v]) => v) as [string, string][]).toString();
      return request<ParkingLotListResponse>('GET', `/parking-lot?${q}`);
    },
    count: () => request<ParkingLotCountResponse>('GET', '/parking-lot/count'),
    get: (itemId: string) => request<ParkingLotDetailResponse>('GET', `/parking-lot/${itemId}`),
    create: (body: Partial<CreateParkingLotItemRequest> & { title: string }) => request<ParkingLotItemResponse>('POST', '/parking-lot', body),
    fromConversation: (conversationId: string, clientTurnId: string) =>
      request<ParkFromConversationResponse>('POST', `/conversations/${conversationId}/parking-lot`, { clientTurnId }),
    update: (itemId: string, body: UpdateParkingLotItemRequest) => request<ParkingLotItemResponse>('PATCH', `/parking-lot/${itemId}`, body),
    comment: (itemId: string, body: string) => request<PostParkingLotCommentResponse>('POST', `/parking-lot/${itemId}/comments`, { body }),
    build: (itemId: string) => request<BuildParkingLotItemResponse>('POST', `/parking-lot/${itemId}/build`),
    /** Part 2: send the drafted requirements to Loop for John's or Adam's approval. */
    sendToLoop: (itemId: string, body: SendToLoopRequest) => request<ParkingLotDetailResponse>('POST', `/parking-lot/${itemId}/send-to-loop`, body),
    talk: (itemId: string) => request<TalkParkingLotItemResponse>('POST', `/parking-lot/${itemId}/conversation`),
    /** Continue the conversation: an idea project's latest conversation, or a new one in it. */
    continueIdea: (itemId: string) => request<TalkParkingLotItemResponse>('POST', `/parking-lot/${itemId}/continue`),
    /** Use this: apply Jabberr's suggestion as the caller's own edit. */
    useSuggestion: (itemId: string, commentId: string) => request<UseSuggestionResponse>('POST', `/parking-lot/${itemId}/comments/${commentId}/use`),
    /** New idea by talking: a conversation (in a private idea project for a new project idea) with its first turn. */
    startIdea: (body: Partial<StartIdeaRequest>) => request<StartIdeaResponse>('POST', '/parking-lot/ideas', body),
  },

  preferences: {
    get: () => request<PreferencesResponse>('GET', '/me/preferences'),
    update: (patch: Preferences) => request<PreferencesResponse>('PATCH', '/me/preferences', patch),
  },

  /** The caller's latest threads across projects (the sidebar's Recent). */
  recentConversations: (limit = 5) => request<RecentConversationsResponse>('GET', `/me/recent-conversations?limit=${limit}`),

  conversations: {
    mentionCandidates: (conversationId: string) => request<{ candidates: Array<{ id: string; name: string; email: string }> }>('GET', `/conversations/${conversationId}/mention-candidates`),
    list: (projectId: string) => request<ConversationListResponse>('GET', `/projects/${projectId}/conversations`),
    create: (projectId: string, body: CreateConversationRequest) =>
      request<ConversationResponse>('POST', `/projects/${projectId}/conversations`, body),
    get: (conversationId: string) => request<ConversationDetailResponse>('GET', `/conversations/${conversationId}`),
    update: (conversationId: string, body: UpdateConversationRequest) =>
      request<ConversationResponse>('PATCH', `/conversations/${conversationId}`, body),
    cancel: (conversationId: string, turnId: string) =>
      request<CancelTurnResponse>('POST', `/conversations/${conversationId}/turns/${turnId}/cancel`),
    /** Appends the turn and answers 201 JSON at once ({userTurnId, seq, assistantTurnId, replied});
     *  the reply itself streams through the conversation's events channel. 409 on a duplicate
     *  clientTurnId or when a reply is wanted while another generation runs. */
    send: (conversationId: string, body: SendMessageRequest): Promise<SendMessageResponse> =>
      request<SendMessageResponse>('POST', `/conversations/${conversationId}/messages`, body),
    /** Re-runs generation for an existing user turn with a fresh assistant turn; the reply streams
     *  through the events channel. 409 not_latest_user_turn / generation_in_flight. */
    regenerate: (conversationId: string, userTurnId: string): Promise<RegenerateResponse> =>
      request<RegenerateResponse>('POST', `/conversations/${conversationId}/turns/${userTurnId}/regenerate`),
    /** PUT /api/conversations/:id/seen (204): the server records read state; unreadMention clears.
     *  Dispatches 'jabberr:seen' so the mentions hook can refresh. */
    seen: async (conversationId: string): Promise<undefined> => {
      const res = await request<undefined>('PUT', `/conversations/${conversationId}/seen`);
      window.dispatchEvent(new CustomEvent('jabberr:seen', { detail: { conversationId } }));
      return res;
    },
  },

  recordings: {
    list: (projectId: string) => request<RecordingListResponse>('GET', `/projects/${projectId}/recordings`),
    create: (projectId: string, body: CreateRecordingRequest) =>
      request<CreateRecordingResponse>('POST', `/projects/${projectId}/recordings`, body),
    putChunk: async (recordingId: string, seq: number, blob: Blob, signal?: AbortSignal): Promise<ChunkUploadResponse> => {
      const init: RequestInit = {
        method: 'PUT',
        headers: { 'content-type': 'application/octet-stream' },
        credentials: 'same-origin',
        body: blob,
      };
      if (signal) init.signal = signal;
      const res = await fetch(`/api/recordings/${recordingId}/chunks/${seq}`, init);
      if (res.status === 401) unauthorizedListeners.forEach((fn) => fn());
      if (!res.ok) throw await readError(res);
      return (await res.json()) as ChunkUploadResponse;
    },
    complete: (recordingId: string, body: CompleteRecordingRequest) =>
      request<RecordingDetailResponse>('POST', `/recordings/${recordingId}/complete`, body),
    importVtt: (projectId: string, body: ImportVttRequest) =>
      request<RecordingDetailResponse>('POST', `/projects/${projectId}/recordings/vtt`, body),
    get: (recordingId: string) => request<RecordingDetailResponse>('GET', `/recordings/${recordingId}`),
    updateSpeakers: (transcriptId: string, body: UpdateSpeakersRequest) =>
      request<TranscriptResponse>('PATCH', `/transcripts/${transcriptId}/speakers`, body),
    review: (recordingId: string) => request<ReviewRecordingResponse>('POST', `/recordings/${recordingId}/review`),
  },

  meetings: {
    /** The meeting's shared assistant artwork; the starter or a project owner may change it. */
    appearance: (meetingId: string) => request<{ jabberrAvatarId: string; jabberrAssistantImageId?: string }>('GET', `/meetings/${meetingId}/appearance`),
    setAppearance: (meetingId: string, jabberrAvatarId: string) =>
      request<{ jabberrAvatarId: string; jabberrAssistantImageId?: string }>('PATCH', `/meetings/${meetingId}/appearance`, { jabberrAvatarId }),
    participation: (id: string) => request<import('@jabberr/core').MeetingParticipationResponse>('GET', `/meetings/${id}/participation`),
    signal: (id: string, action: 'answer' | 'stop') => request<import('@jabberr/core').MeetingParticipationResponse>('POST', `/meetings/${id}/participation`, { action }),
    corpus: (id: string, payload: CallCorpusRequest) => request<{ corpusId: string; state: string; duplicate: boolean }>('POST', `/meetings/${id}/corpus`, payload),
    feedback: (id: string, responseId: string, rating: 'useful' | 'not_needed' | 'bad_timing') => request<undefined>('POST', `/meetings/${id}/feedback`, { responseId, rating }),
    create: (projectId: string, body?: CreateMeetingRequest) =>
      request<CreateMeetingResponse>('POST', `/projects/${projectId}/meetings`, body),
    list: (projectId: string) => request<MeetingListResponse>('GET', `/projects/${projectId}/meetings`),
    get: (meetingId: string) => request<MeetingResponse>('GET', `/meetings/${meetingId}`),
    transcript: (meetingId: string, after: number) =>
      request<MeetingTranscriptResponse>('GET', `/meetings/${meetingId}/transcript?after=${after}`),
    update: (meetingId: string, body: UpdateMeetingRequest) =>
      request<{ meeting: { listenMode: string; title: string } }>('PATCH', `/meetings/${meetingId}`, body),
    end: (meetingId: string) => request<{ meeting: { status: MeetingStatus; recordingId: string | null } }>('POST', `/meetings/${meetingId}/end`),
    token: (meetingId: string) => request<{ url: string; token: string }>('POST', `/meetings/${meetingId}/room/token`),
    inviteJabberr: (meetingId: string) =>
      request<RoomInviteResponse>('POST', `/meetings/${meetingId}/room/jabberr`),
    removeJabberr: (meetingId: string) => request<undefined>('DELETE', `/meetings/${meetingId}/room/jabberr`),
    invitePerson: (meetingId: string, userId: string) =>
      request<undefined>('POST', `/meetings/${meetingId}/room/invite`, { userId }),
  },
};

export function errorMessage(err: unknown): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error) return err.message;
  return String(err);
}
